// ============================================================================
// JSON-LD builders — pure data, no React.
//
// Extracted from components/Seo.jsx so ONE definition serves both:
//   • the app at runtime (Seo.jsx re-exports everything here, so every existing
//     `import { productJsonLd } from "../components/Seo"` keeps working)
//   • scripts/prerender-seo.mjs at build time, which bakes the same schema into
//     the static HTML so crawlers see it without executing the bundle
//
// KEEP THIS FILE DEPENDENCY-FREE. The build script imports it directly in Node
// (via a temp .mjs copy), so a React or CSS import here would break the build.
// ============================================================================

const SITE = "https://staketek.com.au";

/**
 * Breadcrumb JSON-LD helper.
 * crumbs = [{name, path}], e.g. [{name:"Home",path:"/"}, {name:"Brackets",path:"/products/brackets"}]
 */
export function breadcrumbJsonLd(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${SITE}${c.path}`,
    })),
  };
}

/**
 * FAQPage JSON-LD helper — eligible for FAQ rich results in Google.
 * faqs = [{ q, a }]
 */
export function faqPageJsonLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/**
 * Article / BlogPosting JSON-LD helper.
 */
export function articleJsonLd({ title, description, image, path, datePublished, dateModified, author = "StakeTek" }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    image: image ? `${SITE}${image}` : `${SITE}/brand/website-hero.jpg`,
    author: { "@type": "Organization", name: author },
    publisher: {
      "@type": "Organization",
      name: "StakeTek",
      logo: { "@type": "ImageObject", url: `${SITE}/brand/logo-horizontal.png` },
    },
    datePublished,
    dateModified: dateModified || datePublished,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}${path}` },
  };
}

/**
 * HowTo JSON-LD helper — eligible for HowTo rich results.
 * steps = [{ name, text }]
 */
export function howToJsonLd({ name, description, image, steps, supplies = [], tools = [] }) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name,
    description,
    image: image ? `${SITE}${image}` : undefined,
    supply: supplies.map((s) => ({ "@type": "HowToSupply", name: s })),
    tool: tools.map((t) => ({ "@type": "HowToTool", name: t })),
    step: steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

// AU only. Rates are weight-based (base A$10.95 domestic 0–1kg) — the "from"
// figure here is the lowest tier. Most orders dispatch in 2–5 business days
// (handlingTime, padded so large orders/busy periods don't breach the promise);
// carrier transit is the rest. New Zealand is intentionally NOT declared here:
// NZ is quote-only (manual freight quote), so we don't advertise an automatic
// NZ rate for Shopping ads.
const auShippingDetails = (handlingDays) => [
  {
    "@type": "OfferShippingDetails",
    shippingRate: { "@type": "MonetaryAmount", value: "10.95", currency: "AUD" },
    shippingDestination: { "@type": "DefinedRegion", addressCountry: "AU" },
    deliveryTime: {
      "@type": "ShippingDeliveryTime",
      // handlingDays lets batch-made products (e.g. the flat bracket)
      // declare their longer dispatch window honestly.
      handlingTime: {
        "@type": "QuantitativeValue",
        minValue: handlingDays?.min ?? 2,
        maxValue: handlingDays?.max ?? 5,
        unitCode: "DAY",
      },
      transitTime: { "@type": "QuantitativeValue", minValue: 2, maxValue: 7, unitCode: "DAY" },
    },
  },
];

// Declares the on-site returns policy for Google (clears the GSC "missing
// hasMerchantReturnPolicy" warning) WITHOUT advertising a 30-day change-of-mind
// window. StakeTek does not accept change-of-mind returns; faulty/damaged/incorrect
// items are handled under Australian Consumer Law (a separate legal right, not a
// merchant return offer), so the correct category here is MerchantReturnNotPermitted.
const AU_RETURN_POLICY = {
  "@type": "MerchantReturnPolicy",
  applicableCountry: "AU",
  returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
};

/**
 * aggregateRating + review fields, shared by Product and ProductGroup nodes.
 *
 * Only ever from REAL reviews (data/reviews.js). The review objects come from
 * the same source file as the aggregate, so schema can never cite a review the
 * site doesn't display. Only rated entries are passed in; text and date are
 * optional. Returns {} when there's nothing to publish.
 */
function reviewFieldsJsonLd({ aggregateRating, reviews }) {
  return {
    ...(aggregateRating && Number(aggregateRating.reviewCount) > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: aggregateRating.ratingValue,
            reviewCount: aggregateRating.reviewCount,
            bestRating: 5,
            worstRating: 1,
          },
        }
      : {}),
    ...(Array.isArray(reviews) && reviews.length > 0
      ? {
          review: reviews.map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.name },
            reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
            ...(r.text ? { reviewBody: r.text } : {}),
            ...(r.date ? { datePublished: r.date } : {}),
          })),
        }
      : {}),
  };
}

/**
 * Product JSON-LD helper.
 */
// `sellable: false` publishes the Product WITHOUT an offers block.
//
// Google Merchant Center builds listings straight from a page's structured
// data, so a Product carrying a price and InStock is enough to create a
// Shopping listing on its own, with no feed entry involved. That put the tube
// post into Shopping at $10.95 "In stock online" while it wasn't for sale.
// Anything not actually purchasable must not advertise an offer.
export function productJsonLd({ name, description, image, price, priceFrom, sku, mpn, path, brand = "StakeTek", material, weightGrams, handlingDays, aggregateRating, reviews, availability = "https://schema.org/InStock", category = "Fencing & Gate Hardware", keywords, sellable = true }) {
  const productUrl = path ? `${SITE}${path}` : `${SITE}/products/${sku?.toLowerCase().replace(/\s+/g, "-")}`;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    // Always present so Google never flags a missing image (falls back to brand hero).
    image: image ? `${SITE}${image}` : `${SITE}/brand/website-hero.jpg`,
    sku,
    // StakeTek parts carry no GTIN/EAN, so the part code is the manufacturer
    // number. Declaring it lets Google match the site listing to the same item
    // in the Merchant Center feed (which sends the identical g:mpn).
    //
    // Deliberately NOT defaulted from `sku`: some pages pass a page slug there
    // ("brackets") rather than a part code, and publishing that as an MPN would
    // hand Google a fake identifier that matches nothing in the feed. Pass mpn
    // explicitly, and only where the product really is in google-feed.xml.
    ...(mpn ? { mpn } : {}),
    // Topical signals — helps Google understand the product's category and the
    // related terms it's relevant for, beyond the exact product name.
    category,
    ...(keywords ? { keywords } : {}),
    ...(material ? { material } : {}),
    ...(weightGrams ? { weight: { "@type": "QuantitativeValue", value: weightGrams, unitCode: "GRM" } } : {}),
    brand: { "@type": "Brand", name: brand },
    manufacturer: { "@type": "Organization", name: brand },
    mainEntityOfPage: { "@type": "WebPage", "@id": productUrl },
    // Only ever from REAL reviews (data/reviews.js). Nested in the Product so Google
    // can show a star rating without creating a second Product node. Never fabricate.
    ...reviewFieldsJsonLd({ aggregateRating, reviews }),
    ...(sellable ? { offers: {
      "@type": "Offer",
      priceCurrency: "AUD",
      price: priceFrom ?? price,
      itemCondition: "https://schema.org/NewCondition",
      availability,
      seller: { "@type": "Organization", name: brand },
      url: productUrl,
      shippingDetails: auShippingDetails(handlingDays),
      hasMerchantReturnPolicy: AU_RETURN_POLICY,
    } } : {}),
  };
}

/**
 * ProductGroup JSON-LD helper — for a product sold in several sizes on ONE page
 * (the fence height extensions: 450 / 600 / 900 mm behind a single Length option).
 *
 * A plain Product node can only ever declare one price, so a multi-length page
 * has to advertise a single "from" figure and hope Google infers the rest.
 * ProductGroup is Google's documented shape for exactly this case: the group
 * carries the shared description, and each length is a real variant with its own
 * price, SKU and weight. That's what makes the whole ladder eligible for
 * merchant listings instead of just the cheapest length.
 *
 * variants = [{ size, price, sku, weightGrams }] — one per length, priced in AUD.
 */
export function productGroupJsonLd({
  name,
  description,
  images = [],
  variants = [],
  path,
  productGroupId,
  brand = "StakeTek",
  material,
  handlingDays,
  availability = "https://schema.org/InStock",
  category = "Fencing & Gate Hardware",
  keywords,
  variesBy = "https://schema.org/size",
  aggregateRating,
  reviews,
}) {
  const productUrl = `${SITE}${path}`;
  const absolute = (src) => (src?.startsWith("http") ? src : `${SITE}${src}`);
  const imageUrls = images.length ? images.map(absolute) : [`${SITE}/brand/website-hero.jpg`];

  return {
    "@context": "https://schema.org",
    "@type": "ProductGroup",
    name,
    description,
    image: imageUrls,
    category,
    ...(keywords ? { keywords } : {}),
    ...(material ? { material } : {}),
    brand: { "@type": "Brand", name: brand },
    manufacturer: { "@type": "Organization", name: brand },
    mainEntityOfPage: { "@type": "WebPage", "@id": productUrl },
    productGroupID: productGroupId,
    variesBy,
    // Reviews sit on the group, not a variant: customers rate the extension,
    // and Google reads a ProductGroup's rating as covering all its lengths.
    ...reviewFieldsJsonLd({ aggregateRating, reviews }),
    hasVariant: variants.map((v) => ({
      "@type": "Product",
      name: `${name}, ${v.size}`,
      sku: v.sku,
      mpn: v.sku,
      size: v.size,
      image: imageUrls[0],
      ...(material ? { material } : {}),
      ...(v.weightGrams
        ? { weight: { "@type": "QuantitativeValue", value: v.weightGrams, unitCode: "GRM" } }
        : {}),
      brand: { "@type": "Brand", name: brand },
      offers: {
        "@type": "Offer",
        priceCurrency: "AUD",
        price: Number(v.price).toFixed(2),
        itemCondition: "https://schema.org/NewCondition",
        availability,
        seller: { "@type": "Organization", name: brand },
        // Variants aren't separately addressable, so every offer points at the
        // page that sells them all.
        url: productUrl,
        shippingDetails: auShippingDetails(handlingDays),
        hasMerchantReturnPolicy: AU_RETURN_POLICY,
      },
    })),
  };
}
